import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';
import { MessageSquare, X, Send, Loader2, Bot, User } from 'lucide-react';

/**
 * Reusable floating chat widget for AI agents.
 *
 * Props:
 * - agentType: 'public' | 'user' | 'intel' (determines which endpoint to call)
 * - isAuthenticated: boolean (switches between public and authenticated endpoints)
 * - context: optional object (extra context to pass to the agent, e.g. LOI data)
 * - primaryColor: string (theme color)
 * - position: 'bottom-right' | 'inline' (default: 'bottom-right')
 * - title: string (chat window title)
 * - placeholder: string (input placeholder)
 * - welcomeMessage: string (initial message from the agent)
 */
const ChatWidget = ({
  agentType = 'public',
  isAuthenticated = false,
  context = null,
  primaryColor = '#2B4B6A',
  position = 'bottom-right',
  title = 'Trados Assistant',
  placeholder = 'Type your message...',
  welcomeMessage = 'Hello! How can I help you today?',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: welcomeMessage },
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevWelcomeRef = useRef(welcomeMessage);

  // Reset messages when agent changes (e.g. user logs in/out)
  useEffect(() => {
    if (prevWelcomeRef.current !== welcomeMessage) {
      setMessages([{ role: 'assistant', content: welcomeMessage }]);
      setSessionId(null);
      prevWelcomeRef.current = welcomeMessage;
    }
  }, [welcomeMessage]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const getEndpoint = () => {
    if (agentType === 'intel') return '/admin/intel/chat';
    if (agentType === 'user' || isAuthenticated) return '/intel/chat';
    return '/public/intel/chat';
  };

  const handleSend = async () => {
    const msg = input.trim();
    if (!msg || sending) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setSending(true);

    try {
      const endpoint = getEndpoint();
      const payload = {
        message: msg,
        session_id: sessionId,
        context: context,
      };
      const res = await api.post(endpoint, payload);
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (error) {
      const errMsg = error.response?.status === 401
        ? 'Please log in to chat with our assistant.'
        : 'Sorry, I had trouble processing that. Please try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Inline mode — renders directly without floating button
  if (position === 'inline') {
    return (
      <div className="flex flex-col h-full bg-white rounded-lg border shadow-sm overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center gap-2" style={{ backgroundColor: primaryColor }}>
          <Bot className="w-5 h-5 text-white" />
          <span className="text-white font-medium text-sm">{title}</span>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.map((msg, idx) => (
            <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                msg.role === 'user'
                  ? 'text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
                style={msg.role === 'user' ? { backgroundColor: primaryColor } : {}}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-lg px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-3 border-t">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={sending || !input.trim()}
              className="px-3 py-2 text-white rounded-lg disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const agentPhoto = agentType === 'public'
    ? 'https://trados.sfo3.digitaloceanspaces.com/agents/Brandon-Circle.png'
    : 'https://trados.sfo3.digitaloceanspaces.com/agents/Amanda-Circle.png';

  // Floating mode
  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-[7.5rem] right-6 w-96 h-[500px] bg-white rounded-xl shadow-2xl border flex flex-col overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between" style={{ backgroundColor: primaryColor }}>
            <div className="flex items-center gap-2">
              <img src={agentPhoto} alt="" className="w-6 h-6 rounded-full object-cover" />
              <span className="text-white font-medium text-sm">{title}</span>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'text-white'
                    : 'bg-gray-100 text-gray-800'
                }`}
                  style={msg.role === 'user' ? { backgroundColor: primaryColor } : {}}
                >
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={sending}
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                className="px-3 py-2 text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: primaryColor }}
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center z-50 transition-transform hover:scale-110 border-[3px] border-[#2B4B6A] overflow-hidden"
        style={{ backgroundColor: isOpen ? primaryColor : 'transparent' }}
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <img src={agentPhoto} alt="" className="w-full h-full object-cover" />
        )}
      </button>
    </>
  );
};

export default ChatWidget;
